import { useCallback, useRef, useState } from 'react';

import { flushSync } from 'react-dom';

import {
  fetchPostOnfidoSmsResponse,
  fetchGetOnfidoCheckStatus,
} from '../../../../../../api/lending/edge';
import { useGetStateByStateKey } from '../../../../../../api/lending/lapiHooks';
import { useStateKey } from '../../../../../../hooks/useStateKey.hook';

export type OnfidoStatus =
  | 'NOT_STARTED'
  | 'PENDING'
  | 'APPROVED'
  | 'DENIED'
  | 'ERROR';

export function useOnfido(): {
  dispatchOnfidoCheck: () => Promise<void>;
  onfidoStatus: OnfidoStatus;
} {
  const [onfidoStatus, setOnfidoStatus] = useState<OnfidoStatus>('NOT_STARTED');
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const { stateKey } = useStateKey();
  const { state: customerState } = useGetStateByStateKey();

  const personUid = customerState?.application?.people?.[0]?.uid;
  const phoneUid = customerState?.application?.people?.[0]?.phones?.[0]?.uid;

  const startPolling = () => {
    const intervalRef = setInterval(async () => {
      await flushSync(() => fetchAndProcessOnfidoCheckStatus());
    }, 10000);
    pollingIntervalRef.current = intervalRef;
  };

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef) {
      clearInterval(pollingIntervalRef.current as NodeJS.Timeout);
    }
  }, [pollingIntervalRef]);

  /**
   * Dispatches a request to Django to create a check for the user
   */
  const dispatchOnfidoCheck = async () => {
    try {
      flushSync(() => {
        setOnfidoStatus('PENDING');
      });
      await fetchPostOnfidoSmsResponse({ stateKey, personUid, phoneUid });
      startPolling();
    } catch (e) {
      flushSync(() => {
        setOnfidoStatus('ERROR');
      });
    }
  };

  /**
   * Granular descriptions for what happened with a result when it is CLEAR
   *
   * Please refer to the documentation in code for specific descriptions of each status
   *
   * @param status Status from onfido on CLEAR result
   */
  const resolveStatus = useCallback(
    (status: number) => {
      switch (status) {
        case 1:
          setOnfidoStatus('PENDING');
          break;
        case 2:
          setOnfidoStatus('PENDING');
          break;
        case 3:
          setOnfidoStatus('PENDING');
          break;
        case 4:
          setOnfidoStatus('APPROVED');
          stopPolling();
          break;
        case 5:
          setOnfidoStatus('ERROR');
          stopPolling();
          break;
        case 6:
          setOnfidoStatus('ERROR');
          stopPolling();
          break;
        default:
          break;
      }
    },
    [stopPolling],
  );

  /**
   * Parses the response from the Onfido check endpoint
   *
   * Please refer to the documentation in code for specific descriptions of each result
   *
   * @param result Result Status from Onfido Check Endpoint
   */
  const resolveResult = useCallback(
    (result: number, status: number) => {
      switch (result) {
        case null:
          break;
        case 1:
          resolveStatus(status);
          break;
        case 2:
          setOnfidoStatus('DENIED');
          stopPolling();
          break;
        default:
          break;
      }
    },
    [resolveStatus, stopPolling],
  );

  const fetchAndProcessOnfidoCheckStatus = useCallback(async () => {
    try {
      const onfidoFetchAndCheckStatusResponseData =
        await fetchGetOnfidoCheckStatus({ stateKey, personUid });

      const {
        data: { result, status },
      } = onfidoFetchAndCheckStatusResponseData;

      resolveResult(result, status);
    } catch (e) {
      setOnfidoStatus('ERROR');
      stopPolling();
    }
  }, [personUid, stateKey, resolveResult, stopPolling]);

  return {
    dispatchOnfidoCheck,
    onfidoStatus,
  };
}
